/**
 * IntakeBoard — the Build tab (spec §0/§1).
 * On /fusion sandbox: path chooser first (demos vs new case). On a real Case Canvas
 * (or after demos chosen): guided templates then evidence cards.
 */
import { useState } from 'react';
import type cytoscape from 'cytoscape';
import { Plus, RotateCcw, UserRound } from 'lucide-react';
import { EVIDENCE, STATUS_META, type InputField } from '../../../case-canvas/registry';
import { useIntakeStore, newIntakeId } from '../../../stores/useIntakeStore';
import { setSubjectName as applySubjectName } from '../../../case-canvas/entityJoin';
import { BuildPathChooser } from './BuildPathChooser';
import { GuidedStart } from './GuidedStart';
import { useCaseWriteBlock } from '../../../case-canvas/caseAccess';
import { EvidenceCard } from './EvidenceCard';

function defaultInputs(fields?: InputField[]): Record<string, unknown> {
  const v: Record<string, unknown> = {};
  (fields || []).forEach((f) => { if (f.kind === 'toggle') v[f.key] = !!f.default; else if (f.kind === 'number') v[f.key] = f.default; });
  return v;
}

export type IntakeBuildPath = 'chooser' | 'demo' | 'case';

type Props = {
  /** Sandbox /fusion only — Case Canvas omits the chooser. */
  showPathChooser?: boolean;
  buildPath?: IntakeBuildPath;
  onExploreDemos?: () => void;
  onCreateCase?: () => void;
  onResetPath?: () => void;
};

export function IntakeBoard({
  showPathChooser = false,
  buildPath = 'case',
  onExploreDemos,
  onCreateCase,
  onResetPath,
}: Props = {}) {
  const started = useIntakeStore((s) => s.started);
  const cards = useIntakeStore((s) => s.cards);
  const addCard = useIntakeStore((s) => s.addCard);
  const reset = useIntakeStore((s) => s.reset);
  const subjectName = useIntakeStore((s) => s.subjectName);
  const setSubjectName = useIntakeStore((s) => s.setSubjectName);
  const [adding, setAdding] = useState(false);
  const { blocked, reason: blockedReason } = useCaseWriteBlock();

  const onSubjectName = (v: string) => {
    setSubjectName(v);
    const cy = (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy;
    if (cy && !cy.destroyed()) applySubjectName(cy, v);
  };

  const startOver = () => {
    reset();
    onResetPath?.();
  };

  if (!started) {
    if (showPathChooser && buildPath === 'chooser' && onExploreDemos && onCreateCase) {
      return (
        <BuildPathChooser
          onExploreDemos={onExploreDemos}
          onCreateCase={onCreateCase}
        />
      );
    }
    return <GuidedStart training={buildPath === 'demo' || showPathChooser} />;
  }

  const add = (rid: string) => {
    const ev = EVIDENCE.find((e) => e.id === rid);
    addCard({ id: newIntakeId(), registryId: rid, inputs: defaultInputs(ev?.inputs), status: 'idle' });
    setAdding(false);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-card">
        {(buildPath === 'demo' || showPathChooser) && (
          <span
            className="shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-100"
            title="Training sandbox — not a live FIR case"
          >
            Demo path
          </span>
        )}
        <div className="relative">
          <button onClick={() => setAdding((a) => !a)} disabled={blocked} title={blocked ? blockedReason : undefined}
            data-testid="intake-add-evidence"
            className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-brand-gold-hover disabled:opacity-50 disabled:cursor-not-allowed">
            <Plus size={13} /> Add evidence
          </button>
          {adding && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setAdding(false)} />
              <div className="absolute z-30 mt-1 w-64 max-h-80 overflow-y-auto rounded-lg border border-border bg-card shadow-xl text-xs">
                {EVIDENCE.map((e) => (
                  <button key={e.id} onClick={() => add(e.id)} className="w-full flex items-start gap-2 px-3 py-1.5 text-left hover:bg-secondary/60">
                    <e.icon size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-1.5"><span className="font-medium truncate">{e.name}</span>
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: STATUS_META[e.status].color }} /></span>
                      <span className="block text-[10px] text-muted-foreground truncate">{e.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <span className="text-[11px] text-muted-foreground">{cards.length} card{cards.length !== 1 ? 's' : ''}</span>
        <div className="ml-auto flex items-center gap-1.5" title="Names the Subject star on the canvas">
          <UserRound size={13} className="text-muted-foreground shrink-0" />
          <input
            value={subjectName}
            onChange={(e) => onSubjectName(e.target.value)}
            placeholder="Subject / suspect name"
            className="w-44 text-[11px] px-2 py-1 bg-background border border-input rounded focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
          />
        </div>
        <button onClick={startOver} className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground px-2 py-1 rounded border border-border">
          <RotateCcw size={11} /> Start over
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {cards.length === 0 ? (
          <div className="h-full grid place-items-center text-center text-xs text-muted-foreground" data-testid="intake-blank-board">
            <div>
              Empty board. Click <span className="font-medium text-foreground">Add evidence</span> to drop the first card,
              <br />fill its inputs, and Run — results land on the Canvas tab.
              <p className="mt-3 text-[10px] max-w-sm mx-auto">
                Tip: Start over → pick a guided template if you want a pre-wired first card.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3 items-start">
            {cards.map((c) => <EvidenceCard key={c.id} card={c} />)}
          </div>
        )}
      </div>
    </div>
  );
}
